/**
 * Coach — assistant conversationnel — CDC §3.6.3.
 *
 * Chat ancré sur les données personnelles (RAG). Chaque réponse cite ses
 * sources. Le filtre de sécurité (§7.1) est appliqué côté serveur : une
 * question évoquant une urgence reçoit une réponse de redirection.
 */
import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'

import { santeApi } from '../api/santeApi'
import { EmptyState, HEALTH_ACCENT, Loading, cardSx } from './shared'

export default function CoachView() {
  const [conversation, setConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const endRef = useRef(null)

  useEffect(() => {
    ;(async () => {
      const convs = await santeApi.conversations.list()
      if (convs.length > 0) {
        const full = await santeApi.conversations.get(convs[0].id)
        setConversation(full)
        setMessages(full.messages || [])
      }
      setLoading(false)
    })()
  }, [])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const startConversation = async () => {
    const conv = await santeApi.conversations.create({
      title: 'Conversation santé',
    })
    setConversation(conv)
    setMessages([])
  }

  const send = async () => {
    const text = input.trim()
    if (!text) return
    let conv = conversation
    if (!conv) {
      conv = await santeApi.conversations.create({ title: 'Conversation santé' })
      setConversation(conv)
    }
    setMessages((prev) => [
      ...prev,
      { id: `tmp-${Date.now()}`, role: 'user', content: text },
    ])
    setInput('')
    setSending(true)
    const answer = await santeApi.askCoach(conv.id, text)
    setSending(false)
    if (answer) setMessages((prev) => [...prev, answer])
  }

  if (loading) return <Loading label="Ouverture du coach…" />

  return (
    <Box>
      <Box
        sx={{
          ...cardSx,
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 700 }}>
            Votre coach santé
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
            Posez vos questions — les réponses s’appuient sur vos données et
            citent leurs sources. Le coach n’établit ni diagnostic ni
            prescription.
          </Typography>
        </Box>
        <IconButton onClick={startConversation} title="Nouvelle conversation">
          <AddRoundedIcon sx={{ color: HEALTH_ACCENT }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          ...cardSx,
          minHeight: 360,
          maxHeight: 460,
          overflowY: 'auto',
          mb: 2,
        }}
      >
        {messages.length === 0 && (
          <EmptyState>
            Exemple : « Quel a été mon poids minimum cette année ? » ou
            « Résume ma dernière analyse de sang ».
          </EmptyState>
        )}
        <Stack spacing={1.5}>
          {messages.map((m) => (
            <Box
              key={m.id}
              sx={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                bgcolor:
                  m.role === 'user'
                    ? 'rgba(61,220,151,0.16)'
                    : 'rgba(255,255,255,0.06)',
                borderRadius: 2,
                px: 2,
                py: 1.25,
              }}
            >
              <Typography
                sx={{ color: '#fff', fontSize: 14, whiteSpace: 'pre-wrap' }}
              >
                {m.content}
              </Typography>
              {m.role === 'assistant' && (m.sources || []).length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  {m.sources.map((s, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={s.label || s.type}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.08)',
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: 11,
                        height: 22,
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          ))}
          {sending && (
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
              Le coach réfléchit…
            </Typography>
          )}
          <div ref={endRef} />
        </Stack>
      </Box>

      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          placeholder="Votre question santé…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send()
            }
          }}
        />
        <Button
          variant="contained"
          onClick={send}
          disabled={sending}
          sx={{ bgcolor: HEALTH_ACCENT, color: '#00001d', minWidth: 56 }}
        >
          <SendRoundedIcon />
        </Button>
      </Stack>
    </Box>
  )
}
